import { isAuthEnabled } from '@/env';

/**
 * phase-dev-identity-and-user-switcher — the dev identity store.
 *
 * Holds which SEEDED dev identity the local session runs as (or null = the
 * legacy `dev@local` all-permissions default). `apiFetch` attaches it as the
 * `x-dev-user` header on every request; the backend honours that header ONLY
 * when auth is disabled, so in a deployed build this module is inert even if
 * it were somehow invoked.
 *
 * Persistence is localStorage so a chosen identity survives reloads — like a
 * real session would — until explicitly switched.
 */

const STORAGE_KEY = 'autoectd.dev-identity';

export function getDevIdentityEmail(): string | null {
  if (isAuthEnabled()) return null;
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/** Set (email) or clear (null) the active dev identity. */
export function setDevIdentityEmail(email: string | null): void {
  try {
    if (email === null) globalThis.localStorage?.removeItem(STORAGE_KEY);
    else globalThis.localStorage?.setItem(STORAGE_KEY, email);
  } catch {
    /* storage unavailable — the switch simply won't persist */
  }
}

/** Headers to merge into API requests (empty outside dev / when unswitched). */
export function devIdentityHeaders(): Record<string, string> {
  const email = getDevIdentityEmail();
  return email ? { 'x-dev-user': email } : {};
}

/**
 * The ONE header builder for hand-rolled `fetch` calls that bypass `apiFetch`.
 *
 * A few call sites cannot use `apiFetch` — SSE streams need `fetch` +
 * ReadableStream because `EventSource` cannot carry `Authorization`. Each of
 * those previously assembled headers by hand, and every one of them forgot the
 * dev-identity header. That produced a split identity: the request that went
 * through `apiFetch` ran as the switched dev user while the hand-rolled one
 * fell back to `dev@local`, so lookups scoped to the caller 404'd for every
 * non-default identity (phase-dev-identity-assistant).
 *
 * Per the repo's one-evaluator-per-rule convention, raw-fetch header assembly
 * lives here and nowhere else. A structural test pins every raw `fetch(` in
 * v2Client.ts to this function so a new streaming endpoint cannot silently
 * omit the identity again.
 *
 * Inert outside dev: `devIdentityHeaders()` returns {} when auth is enabled
 * or no identity is selected, and the backend only reads `x-dev-user` when
 * auth is disabled.
 */
export function rawFetchHeaders(
  token: string | null | undefined,
  extra: Record<string, string> = {},
): Record<string, string> {
  return {
    ...extra,
    ...devIdentityHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * The DELIBERATE initials rule (pinned by test): the first character of each
 * of the first two whitespace-separated words of the display name, uppercased
 * ("Test Admin1" → "TA"); a single-word name falls back to its first two
 * characters uppercased ("dev@local" → "DE"). Initials derive from the NAME
 * field only — never from the role.
 */
export function devInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]![0] ?? ''}${words[1]![0] ?? ''}`.toUpperCase();
  }
  return (words[0] ?? '').slice(0, 2).toUpperCase();
}
